(function () {
  if (customElements.get('testimonials-section')) return;

  class TestimonialsSection extends HTMLElement {
    connectedCallback() {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.paused = false;
      this.carousels = [];

      this.setupFilter();
      this.setupQuotes();
      this.setupVideos();
      this.setupCarousels();

      if ('ResizeObserver' in window) {
        // A wider track can fit every card, so re-check the clamps and the arrows when the width changes.
        this.resizeObserver = new ResizeObserver(() => {
          this.measureQuotes();
          this.carousels.forEach((carousel) => this.updateArrows(carousel));
        });
        this.resizeObserver.observe(this);
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          this.stopAutoplay();
          this.showArea('all');
          const card = event.target.closest('[data-card]');
          const carousel = card && this.carousels.find((item) => item.cards.includes(card));
          if (carousel) this.scrollToCard(carousel, card);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* ----- All / Stories / Videos ----- */
    setupFilter() {
      this.filters = Array.from(this.querySelectorAll('[data-filter]'));
      this.filters.forEach((button) => button.addEventListener('click', () => this.showArea(button.dataset.filter)));
    }

    showArea(name) {
      if (!this.filters || !this.filters.length) return;
      this.filters.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.filter === name)));
      this.querySelectorAll('[data-area]').forEach((area) => { area.hidden = name !== 'all' && area.dataset.area !== name; });
      this.carousels.forEach((carousel) => this.updateArrows(carousel));
      this.measureQuotes();
    }

    /* ----- Long quotes: clamp, with a Read more toggle only where the text really overflows ----- */
    setupQuotes() {
      this.quotes = Array.from(this.querySelectorAll('.tst__stories--carousel [data-quote]'));
      this.quotes.forEach((quote) => {
        const button = quote.parentElement.querySelector('[data-more]');
        if (!button) return;
        quote.classList.add('is-clamped');
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!open));
          button.querySelector('.tst-card__more-label').textContent = open ? button.dataset.moreLabel : button.dataset.lessLabel;
          quote.classList.toggle('is-clamped', open);
          this.stopAutoplay();
        });
      });
      this.measureQuotes();
    }

    measureQuotes() {
      (this.quotes || []).forEach((quote) => {
        const button = quote.parentElement.querySelector('[data-more]');
        if (!button || button.getAttribute('aria-expanded') === 'true') return;
        button.hidden = quote.scrollHeight <= quote.clientHeight + 2;
      });
    }

    /* ----- Videos: nothing from the YouTube player loads until a poster is clicked ----- */
    setupVideos() {
      this.querySelectorAll('[data-play]').forEach((button) => {
        button.addEventListener('click', () => {
          const id = button.dataset.videoId;
          if (!id) return;
          const iframe = document.createElement('iframe');
          iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1';
          iframe.title = button.dataset.title || '';
          iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
          iframe.setAttribute('allowfullscreen', '');
          const media = button.closest('[data-media]');
          media.innerHTML = '';
          media.appendChild(iframe);
          iframe.focus();
          this.stopAutoplay();
        });
      });
    }

    /* ----- Carousels (native scroll-snap tracks): the stories row and the videos row ----- */
    setupCarousels() {
      this.carousels = Array.from(this.querySelectorAll('[data-carousel]')).map((area) => {
        const track = area.querySelector('[data-track]');
        const carousel = {
          area,
          track,
          cards: Array.from(track.querySelectorAll('[data-card]')),
          prev: area.querySelector('[data-prev]'),
          next: area.querySelector('[data-next]'),
          nav: area.querySelector('.tst__nav')
        };
        if (carousel.prev) carousel.prev.addEventListener('click', () => { this.stopAutoplay(); this.step(carousel, -1); });
        if (carousel.next) carousel.next.addEventListener('click', () => { this.stopAutoplay(); this.step(carousel, 1); });
        track.addEventListener('keydown', (event) => {
          if (event.target !== track) return;
          if (event.key === 'ArrowRight') { event.preventDefault(); this.stopAutoplay(); this.step(carousel, 1); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.stopAutoplay(); this.step(carousel, -1); }
        });
        let ticking = false;
        track.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => { ticking = false; this.updateArrows(carousel); });
        }, { passive: true });
        this.updateArrows(carousel);
        return carousel;
      });

      this.stories = this.carousels.find((carousel) => carousel.area.dataset.carousel === 'stories');
      if (!this.stories) return;
      ['mouseenter', 'focusin', 'touchstart'].forEach((type) => this.stories.area.addEventListener(type, () => { this.paused = true; }, { passive: true }));
      ['mouseleave', 'focusout'].forEach((type) => this.stories.area.addEventListener(type, () => { this.paused = false; }));
      if (this.dataset.autoplay === 'true' && !this.reduceMotion && this.stories.cards.length > 1) this.startAutoplay();
    }

    atEnd(carousel) {
      return carousel.track.scrollLeft + carousel.track.clientWidth >= carousel.track.scrollWidth - 4;
    }

    step(carousel, direction) {
      const size = carousel.cards.length > 1 ? carousel.cards[1].offsetLeft - carousel.cards[0].offsetLeft : carousel.track.clientWidth;
      carousel.track.scrollBy({ left: direction * size, behavior: this.reduceMotion ? 'auto' : 'smooth' });
    }

    scrollToCard(carousel, card) {
      carousel.track.scrollTo({ left: card.offsetLeft - carousel.cards[0].offsetLeft, behavior: this.reduceMotion ? 'auto' : 'smooth' });
    }

    updateArrows(carousel) {
      if (carousel.prev) carousel.prev.disabled = carousel.track.scrollLeft <= 4;
      if (carousel.next) carousel.next.disabled = this.atEnd(carousel);
      // Arrows only when there is a second screenful to scroll to.
      if (carousel.nav) carousel.nav.hidden = carousel.track.scrollWidth <= carousel.track.clientWidth + 4;
    }

    startAutoplay() {
      const seconds = Math.max(3, parseInt(this.dataset.speed, 10) || 6);
      const stories = this.stories;
      this.timer = setInterval(() => {
        if (this.paused || document.hidden || !stories.track.offsetParent) return;
        if (this.atEnd(stories)) stories.track.scrollTo({ left: 0, behavior: 'smooth' });
        else this.step(stories, 1);
      }, seconds * 1000);
    }

    stopAutoplay() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }
  }

  customElements.define('testimonials-section', TestimonialsSection);
})();
